import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Icon from '../../../components/AppIcon';

// Fix default marker icon issue with Leaflet
delete L?.Icon?.Default?.prototype?._getIconUrl;
L?.Icon?.Default?.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to update map view when user location changes
const MapUpdater = ({ center }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map?.setView(center, 13);
    }
  }, [center, map]);
  
  return null;
};

const MapView = ({ userLocation, routes, selectedRoute, onRouteSelect }) => {
  const defaultCenter = [37.8746, 32.4932]; // Konya coordinates as fallback
  const center = userLocation 
    ? [userLocation?.latitude, userLocation?.longitude] 
    : defaultCenter;

  // Generate colors for routes
  const routeColors = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#10b981', // green
    '#f59e0b', // amber
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#14b8a6', // teal
    '#f97316', // orange
  ];

  // Filter visible routes and those with valid geometry
  const visibleRoutes = routes?.filter(route => 
    route?.visible && 
    route?.geometry?.coordinates && 
    route?.geometry?.coordinates?.length > 0
  ) || [];

  // Sort routes by distance and determine which should be colored
  const sortedByDistance = [...visibleRoutes]?.sort((a, b) => {
    const distA = a?.distanceToRoute ?? Infinity;
    const distB = b?.distanceToRoute ?? Infinity;
    return distA - distB;
  });

  // Get the 6 closest routes with their assigned colors
  const closestRoutesWithColors = sortedByDistance?.slice(0, 6)?.map((route, index) => ({
    id: route?.id,
    color: routeColors?.[index % routeColors?.length]
  }));

  // Create a map for quick lookup
  const routeColorMap = new Map(
    closestRoutesWithColors?.map(item => [item?.id, item?.color])
  );

  return (
    <div className="w-full space-y-4">
      <div className="w-full h-64 md:h-80 lg:h-96 bg-muted rounded-xl overflow-hidden shadow-md">
        {userLocation ? (
          <MapContainer
            center={center}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapUpdater center={center} />
            
            {/* User location marker */}
            <Marker position={center}>
              <Popup>
                <div className="text-sm">
                  <strong>Konumunuz</strong>
                </div>
              </Popup>
            </Marker>
            
            {/* Route polylines */}
            {visibleRoutes?.map((route) => {
              const coordinates = route?.geometry?.coordinates?.map(coord => [coord?.[1], coord?.[0]]) || [];
              const assignedColor = routeColorMap?.get(route?.id);
              const color = assignedColor || '#9ca3af'; // gray for routes beyond top 6
              const isClosest = !!assignedColor;
              
              return (
                <Polyline
                  key={route?.id}
                  positions={coordinates}
                  pathOptions={{
                    color: color,
                    weight: 4,
                    opacity: isClosest ? 0.7 : 0.3
                  }}
                >
                  {isClosest && coordinates?.length > 0 && (
                    <Tooltip
                      permanent
                      direction="center"
                      offset={[0, 0]}
                      className="route-label-tooltip"
                      sticky={false}
                    >
                      <span style={{
                        backgroundColor: color,
                        color: '#ffffff',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        display: 'inline-block',
                        letterSpacing: '0.3px'
                      }}>
                        {route?.name}
                      </span>
                    </Tooltip>
                  )}
                  <Popup>
                    <div className="text-sm">
                      <strong>{route?.name}</strong>
                      <br />
                      {route?.startPoint} → {route?.endPoint}
                    </div>
                  </Popup>
                </Polyline>
              );
            })}
          </MapContainer>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-muted-foreground">Harita yükleniyor...</p>
            </div>
          </div>
        )}
      </div>
      {routes?.length > 0 && (
        <div className="bg-card rounded-lg p-3 md:p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm md:text-base font-semibold text-foreground">
              Güzergahını görmek istediğiniz hattın üzerine tıklayabilirsiniz. (Konumunuza en yakın hatlar görünür halde açık)
            </h3>
            <span className="text-xs text-muted-foreground">
              {routes?.filter(r => r?.visible)?.length}/{routes?.length} görünür
            </span>
          </div>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {routes?.map((route) => {
              const assignedColor = routeColorMap?.get(route?.id);
              const color = assignedColor || '#9ca3af';
              const isClosest = !!assignedColor;
              return (
                <button
                  key={route?.id}
                  onClick={() => onRouteSelect(route?.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-smooth active-press ${
                    route?.visible
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                  style={route?.visible && isClosest ? { backgroundColor: color, borderColor: color } : route?.visible ? { backgroundColor: '#9ca3af' } : {}}
                >
                  <Icon 
                    name={route?.visible ? "Eye" : "EyeOff"} 
                    size={16} 
                    color={route?.visible ? "#ffffff" : "var(--color-muted-foreground)"} 
                  />
                  <span className="whitespace-nowrap">{route?.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;